import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import styles from "./DashboardLayout.module.css";

type NavItem = { path: string; icon: string; label: string; exact?: boolean };

const NAV_SECTIONS: { label: string | null; items: NavItem[] }[] = [
  {
    label: null,
    items: [{ path: "/v1", icon: "📊", label: "대시보드", exact: true }],
  },
  {
    label: "📚 학습 콘텐츠",
    items: [
      { path: "/v1/words", icon: "📝", label: "단어 관리" },
      { path: "/v1/concept1", icon: "🔀", label: "Concept 1 — A vs B" },
      { path: "/v1/concept2", icon: "✏️", label: "Concept 2 — 빈칸" },
      { path: "/v1/nuance", icon: "↔️", label: "Concept 3/4 — 뉘앙스" },
    ],
  },
  {
    label: "⚙️ 관리",
    items: [
      { path: "/v1/users", icon: "👥", label: "사용자 관리" },
      { path: "/v1/notices", icon: "📢", label: "공지사항" },
      { path: "/v1/todos", icon: "✅", label: "할 일" },
      { path: "/v1/expenses", icon: "💸", label: "운영 비용" },
    ],
  },
  {
    label: "📊 기록",
    items: [
      { path: "/v1/study-records", icon: "📈", label: "학습 기록" },
      { path: "/v1/table/chat_sessions", icon: "💬", label: "채팅 세션" },
      { path: "/v1/login-logs", icon: "🔐", label: "로그인 이력" },
    ],
  },
  {
    label: "🛠 개발",
    items: [
      { path: "/v1/db-explorer", icon: "🗄", label: "DB LIST" },
    ],
  },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? null);
    });
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/login");
  }

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ""}`}>
        <div className={styles.sidebarTop}>
          {/* Logo + toggle */}
          <div className={styles.logoRow}>
            {!collapsed && (
              <NavLink to="/v1" end style={{ textDecoration: "none" }} className={styles.logo}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <polygon points="14,2 17,11 26,11 19,17 22,26 14,20 6,26 9,17 2,11 11,11" fill="#A78BFA" opacity="0.9" />
                </svg>
                <div>
                  <div className={styles.logoTitle}>Gang-Z</div>
                  <div className={styles.logoSub}>ADMIN</div>
                </div>
              </NavLink>
            )}
            <button
              className={styles.collapseBtn}
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? "메뉴 펼치기" : "메뉴 접기"}
            >
              {collapsed ? "▶" : "◀"}
            </button>
          </div>

          <nav className={styles.nav}>
            {NAV_SECTIONS.map((section, si) => (
              <div key={si} className={styles.navGroup}>
                {section.label && !collapsed && (
                  <div className={styles.navSection}>{section.label}</div>
                )}
                {collapsed && si > 0 && (
                  <div className={styles.navDivider} />
                )}
                {section.items.map((t) => (
                  <NavLink
                    key={t.path}
                    to={t.path}
                    end={t.exact}
                    title={collapsed ? t.label : undefined}
                    className={({ isActive }) =>
                      `${styles.navItem} ${collapsed ? styles.navItemCollapsed : ""} ${isActive ? styles.navItemActive : ""}`
                    }
                  >
                    <span className={styles.navIcon}>{t.icon}</span>
                    {!collapsed && t.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </div>

        <div className={`${styles.userInfo} ${collapsed ? styles.userInfoCollapsed : ""}`}>
          {userEmail && !collapsed && (
            <div className={styles.userEmail}>{userEmail}</div>
          )}
          <button className={`${styles.logoutBtn} ${collapsed ? styles.logoutBtnCollapsed : ""}`} onClick={handleLogout}>
            {collapsed ? "🚪" : "🚪 로그아웃"}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`${styles.main} ${collapsed ? styles.mainCollapsed : ""}`}>
        <Outlet />
      </main>
    </div>
  );
}
